import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';
import {
  MUSCLE_GROUPS, EXERCISE_TYPES, muscleGroupLabel, exerciseTypeLabel, cardioMetricLabel,
  CARDIO_TARGET_CONFIG, targetsNaarFormulier, formulierNaarTargets,
} from '../../utils/exerciseUtils';
import { errorText } from '../../utils/errorMessages';
import ConfirmModal from '../../components/ConfirmModal';

// Voorkomt dat scrollen boven een gefocust number-veld de waarde wijzigt
// (zelfde helper als in RecipeFormPage).
function blurOnWheel(e) {
  e.target.blur();
}

function SortableOefeningRow({ id, item, index, onUpdate, onUpdateTarget, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const isCardio = item.exercise_type === 'cardio';

  return (
    <div ref={setNodeRef} style={style} className="template-item-row">
      <div className="template-item-head">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          style={{ cursor: 'grab', touchAction: 'none' }}
          {...attributes}
          {...listeners}
          title={t('trainingTemplates.sleepHandvat')}
        >
          ⠿
        </button>
        <span className="template-item-nummer">{index + 1}</span>
        <div className="template-item-naam">
          <strong>{item.name}</strong>
          <small>
            {muscleGroupLabel(item.muscle_group)}
            {isCardio ? ` · ${exerciseTypeLabel('cardio')}` : ''}
          </small>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onRemove(item._id)} title={t('trainingTemplates.oefeningVerwijderen')}>
          ✕
        </button>
      </div>
      {/* Cardio-oefeningen hebben geen sets/reps/gewicht-suggestie (taakomschrijving
          onderdeel 4: die velden zijn hier niet relevant). */}
      {!isCardio && (
        <div className="template-item-velden">
          <label>
            <span>{t('trainingTemplates.veldSets')}</span>
            <input
              className="form-input"
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              value={item.suggested_sets}
              onWheel={blurOnWheel}
              onChange={e => onUpdate(item._id, 'suggested_sets', e.target.value)}
            />
          </label>
          <label>
            <span>{t('trainingTemplates.veldReps')}</span>
            <input
              className="form-input"
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              value={item.suggested_reps}
              onWheel={blurOnWheel}
              onChange={e => onUpdate(item._id, 'suggested_reps', e.target.value)}
            />
          </label>
          <label>
            <span>{t('trainingTemplates.veldGewicht')}</span>
            <input
              className="form-input"
              type="number"
              min="0"
              step="0.5"
              inputMode="decimal"
              value={item.suggested_weight}
              onWheel={blurOnWheel}
              onChange={e => onUpdate(item._id, 'suggested_weight', e.target.value)}
            />
          </label>
        </div>
      )}
      {/* Onderdeel 13: doelvelden volgen de metrics die in de bibliotheek
          voor déze oefening zijn aangevinkt (exercises.cardio_metrics) —
          niets is hier per oefening hardgecodeerd, dus een nieuwe metric
          verschijnt vanzelf zodra hij aan de bibliotheek wordt toegevoegd. */}
      {isCardio && (
        (item.cardio_metrics || []).length === 0 ? (
          <p className="text-muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
            {t('trainingTemplates.cardioGeenMetrics')}
          </p>
        ) : (
          <div className="template-item-velden">
            {(item.cardio_metrics || []).map(metric => {
              const cfg = CARDIO_TARGET_CONFIG[metric];
              if (!cfg) return null;
              const doelen = item.cardio_targets || {};
              if (cfg.invoer === 'minsec') {
                const waarde = doelen[metric] || { min: '', sec: '' };
                return (
                  <label key={metric}>
                    <span>{t('trainingTemplates.doelVeld', { metric: cardioMetricLabel(metric) })}</span>
                    <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        step="1"
                        inputMode="numeric"
                        aria-label={t('trainingTemplates.doelMinuten')}
                        value={waarde.min}
                        onWheel={blurOnWheel}
                        onChange={e => onUpdateTarget(item._id, metric, { ...waarde, min: e.target.value })}
                      />
                      <span aria-hidden="true">:</span>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        max="59"
                        step="1"
                        inputMode="numeric"
                        aria-label={t('trainingTemplates.doelSeconden')}
                        value={waarde.sec}
                        onWheel={blurOnWheel}
                        onChange={e => onUpdateTarget(item._id, metric, { ...waarde, sec: e.target.value })}
                      />
                    </span>
                  </label>
                );
              }
              return (
                <label key={metric}>
                  <span>
                    {t('trainingTemplates.doelVeld', { metric: cardioMetricLabel(metric) })}
                    {cfg.eenheid ? ` (${cfg.eenheid})` : ''}
                  </span>
                  <input
                    className="form-input"
                    type="number"
                    min="0"
                    step={cfg.step}
                    inputMode="decimal"
                    value={doelen[metric] ?? ''}
                    onWheel={blurOnWheel}
                    onChange={e => onUpdateTarget(item._id, metric, e.target.value)}
                  />
                </label>
              );
            })}
          </div>
        )
      )}
      <div className="template-item-note">
        <label>
          <span>{t('trainingTemplates.veldAandachtspunt')}</span>
          <textarea
            className="form-input"
            rows={2}
            maxLength={500}
            placeholder={t('trainingTemplates.veldAandachtspuntPlaceholder')}
            value={item.coach_note}
            onChange={e => onUpdate(item._id, 'coach_note', e.target.value)}
            style={{ resize: 'vertical', minHeight: 40 }}
          />
        </label>
      </div>
    </div>
  );
}

export default function ClientTemplateFormPage() {
  const navigate = useNavigate();
  const { id: clientId, templateId } = useParams();
  const isNieuw = !templateId;

  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [naam, setNaam] = useState('');
  const [geldigTot, setGeldigTot] = useState('');
  const [items, setItems] = useState([]);
  const [bibliotheek, setBibliotheek] = useState([]);
  const [zoek, setZoek] = useState('');
  const [filterSpiergroep, setFilterSpiergroep] = useState('');
  // Onderdeel 12: filter op oefeningstype (Alle/Kracht/Cardio), te
  // combineren met het bestaande spiergroepfilter.
  const [filterType, setFilterType] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [annuleerConfirm, setAnnuleerConfirm] = useState(false);

  // Rijen krijgen een stabiel client-side id voor dnd-kit: een nieuw
  // toegevoegde oefening heeft nog geen database-id, en na opslaan krijgen
  // alle items sowieso nieuwe id's (de RPC vervangt ze).
  const rijTellerRef = useRef(0);
  function nieuweRijId() { rijTellerRef.current += 1; return `rij-${rijTellerRef.current}`; }

  function markDirty() { setDirty(true); }

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [clientRes, oefeningenRes] = await Promise.all([
          supabase.from('profiles').select('id, full_name').eq('id', clientId).maybeSingle(),
          // Verborgen oefeningen (is_active = false) verdwijnen alleen hier,
          // bij het samenstellen van een nieuw sjabloon. Ze blijven leesbaar
          // en zichtbaar in het bibliotheekoverzicht, en een bestaand
          // sjabloon dat er al naar verwijst laadt gewoon door (die rijen
          // komen uit de template-query hieronder, niet uit deze lijst).
          supabase.from('exercises').select('id, name, muscle_group, exercise_type, cardio_metrics').eq('is_active', true).order('name'),
        ]);
        if (clientRes.error) throw clientRes.error;
        if (oefeningenRes.error) throw oefeningenRes.error;
        setClient(clientRes.data);
        setBibliotheek(oefeningenRes.data || []);

        if (!isNieuw) {
          const { data: tmpl, error: tmplErr } = await supabase
            .from('client_exercise_templates')
            .select('id, name, client_id, status, valid_until, template_exercise_items(id, exercise_id, sort_order, suggested_sets, suggested_reps, suggested_weight, coach_note, cardio_targets, exercises(id, name, muscle_group, exercise_type, cardio_metrics))')
            .eq('id', templateId)
            .maybeSingle();
          if (tmplErr) throw tmplErr;
          if (!tmpl) { setError(t('trainingTemplates.foutNietGevonden')); return; }
          if (tmpl.client_id !== clientId) { setError(t('trainingTemplates.foutAndereClient')); return; }

          setNaam(tmpl.name);
          setGeldigTot(tmpl.valid_until || '');
          setItems([...(tmpl.template_exercise_items || [])]
            .sort((a, b) => a.sort_order - b.sort_order)
            .map(rij => ({
              _id: nieuweRijId(),
              exercise_id: rij.exercise_id,
              name: rij.exercises?.name || '—',
              muscle_group: rij.exercises?.muscle_group || '',
              exercise_type: rij.exercises?.exercise_type || 'kracht',
              cardio_metrics: rij.exercises?.cardio_metrics || [],
              cardio_targets: targetsNaarFormulier(rij.cardio_targets),
              suggested_sets: rij.suggested_sets ?? '',
              suggested_reps: rij.suggested_reps ?? '',
              suggested_weight: rij.suggested_weight ?? '',
              coach_note: rij.coach_note ?? '',
            })));
        }
      } catch (err) {
        setError(t('trainingTemplates.foutLaden') + errorText(err, { scope: 'ClientTemplateForm.laden' }));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, templateId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems(prev => {
      const oud = prev.findIndex(i => i._id === active.id);
      const nieuw = prev.findIndex(i => i._id === over.id);
      return arrayMove(prev, oud, nieuw);
    });
    markDirty();
  }

  function voegToe(oefening) {
    setItems(prev => [...prev, {
      _id: nieuweRijId(),
      exercise_id: oefening.id,
      name: oefening.name,
      muscle_group: oefening.muscle_group,
      exercise_type: oefening.exercise_type || 'kracht',
      cardio_metrics: oefening.cardio_metrics || [],
      cardio_targets: targetsNaarFormulier(null),
      suggested_sets: '',
      suggested_reps: '',
      suggested_weight: '',
      coach_note: '',
    }]);
    markDirty();
  }

  function updateItem(rijId, veld, waarde) {
    setItems(prev => prev.map(i => (i._id === rijId ? { ...i, [veld]: waarde } : i)));
    markDirty();
  }

  function updateTarget(rijId, metric, waarde) {
    setItems(prev => prev.map(i => (
      i._id === rijId ? { ...i, cardio_targets: { ...(i.cardio_targets || {}), [metric]: waarde } } : i
    )));
    markDirty();
  }

  function verwijderItem(rijId) {
    setItems(prev => prev.filter(i => i._id !== rijId));
    markDirty();
  }

  function handleTerug() {
    if (dirty) { setAnnuleerConfirm(true); return; }
    navigate(`/coach/clients/${clientId}`);
  }

  async function handleOpslaan(e) {
    e.preventDefault();
    setError('');
    if (!naam.trim()) { setError(t('trainingTemplates.valNaam')); return; }
    if (items.length === 0) { setError(t('trainingTemplates.valOefeningen')); return; }
    for (const item of items) {
      if (item.suggested_sets !== '' && Number(item.suggested_sets) < 1) { setError(t('trainingTemplates.valSets')); return; }
      if (item.suggested_reps !== '' && Number(item.suggested_reps) < 1) { setError(t('trainingTemplates.valReps')); return; }
      if (item.suggested_weight !== '' && Number(item.suggested_weight) < 0) { setError(t('trainingTemplates.valGewicht')); return; }
    }

    setSaving(true);
    try {
      // Eén RPC: bij bewerken worden alle items in dezelfde transactie
      // vervangen, zodat de volgorde nooit half bijgewerkt achterblijft.
      const { error: err } = await supabase.rpc('save_client_exercise_template', {
        p_template_id: templateId || null,
        p_client_id: clientId,
        p_name: naam.trim(),
        p_valid_until: geldigTot || null,
        p_items: items.map(i => ({
          exercise_id: i.exercise_id,
          suggested_sets: i.exercise_type === 'cardio' || i.suggested_sets === '' ? null : Math.round(Number(i.suggested_sets)),
          suggested_reps: i.exercise_type === 'cardio' || i.suggested_reps === '' ? null : Math.round(Number(i.suggested_reps)),
          suggested_weight: i.exercise_type === 'cardio' || i.suggested_weight === '' ? null : Number(i.suggested_weight),
          coach_note: i.coach_note?.trim() || null,
          // Alleen bij cardio; bij kracht blijft dit bewust NULL zodat een
          // oefening die ooit van type wisselt geen betekenisloze doelen
          // meesleept.
          cardio_targets: i.exercise_type === 'cardio'
            ? formulierNaarTargets(i.cardio_targets, i.cardio_metrics || [])
            : null,
        })),
      });
      if (err) throw err;
      setDirty(false);
      navigate(`/coach/clients/${clientId}`);
    } catch (err) {
      setError(t('trainingTemplates.foutOpslaan') + errorText(err, { scope: 'ClientTemplateForm.opslaan' }));
      setSaving(false);
    }
  }

  // Onderdeel 12 — type- en spiergroepfilter werken samen. Bij "Cardio" is
  // de spiergroep niet relevant (cardio-oefeningen staan in de bibliotheek
  // doorgaans onder 'overig'); het spiergroepfilter wordt daar dan ook
  // uitgezet in plaats van stilzwijgend genegeerd, zodat de coach geen lege
  // lijst krijgt zonder te zien waarom.
  const spiergroepActief = filterType !== 'cardio';
  const gefilterd = bibliotheek.filter(o => {
    if (filterType && (o.exercise_type || 'kracht') !== filterType) return false;
    if (spiergroepActief && filterSpiergroep && o.muscle_group !== filterSpiergroep) return false;
    if (zoek && !o.name.toLowerCase().includes(zoek.toLowerCase())) return false;
    return true;
  });
  const toegevoegdeIds = new Set(items.map(i => i.exercise_id));

  if (loading) {
    return <div className="loading-center"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <button className="btn btn-ghost" onClick={handleTerug} style={{ marginBottom: 12 }}>
          {t('trainingTemplates.terug')}
        </button>
        <h1 className="page-title">
          {isNieuw ? t('trainingTemplates.formTitelNieuw') : t('trainingTemplates.formTitelBewerken')}
        </h1>
        {client && (
          <p className="page-subtitle">{t('trainingTemplates.formSubtitel', { client: client.full_name })}</p>
        )}
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

      <form onSubmit={handleOpslaan}>
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 260px', maxWidth: 420 }}>
              <label className="form-label" htmlFor="sjabloon-naam">{t('trainingTemplates.veldNaam')}</label>
              <input
                id="sjabloon-naam"
                className="form-input"
                value={naam}
                maxLength={120}
                placeholder={t('trainingTemplates.veldNaamPlaceholder')}
                onChange={e => { setNaam(e.target.value); markDirty(); }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 200px', maxWidth: 240 }}>
              <label className="form-label" htmlFor="sjabloon-geldig-tot">{t('trainingTemplates.veldGeldigTot')}</label>
              <input
                id="sjabloon-geldig-tot"
                type="date"
                className="form-input"
                value={geldigTot}
                onChange={e => { setGeldigTot(e.target.value); markDirty(); }}
              />
              <small className="text-muted">{t('trainingTemplates.veldGeldigTotHint')}</small>
            </div>
          </div>
        </div>

        <div className="template-form-layout">
          {/* Gekozen oefeningen, in volgorde */}
          <div className="card">
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{t('trainingTemplates.gekozenTitel')}</h2>
            <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>{t('trainingTemplates.gekozenHint')}</p>

            {items.length === 0 ? (
              <div className="empty-state">
                <div style={{ fontSize: 32 }}>🏋️</div>
                <p>{t('trainingTemplates.geenOefeningenGekozen')}</p>
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={items.map(i => i._id)} strategy={verticalListSortingStrategy}>
                  {items.map((item, index) => (
                    <SortableOefeningRow
                      key={item._id}
                      id={item._id}
                      item={item}
                      index={index}
                      onUpdate={updateItem}
                      onUpdateTarget={updateTarget}
                      onRemove={verwijderItem}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>

          {/* Oefeningenbibliotheek */}
          <div className="card">
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>{t('trainingTemplates.bibliotheekTitel')}</h2>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <input
                type="search"
                className="form-input"
                style={{ flex: '1 1 140px', minWidth: 0 }}
                placeholder={t('exercises.zoekPlaceholder')}
                value={zoek}
                onChange={e => setZoek(e.target.value)}
              />
              <select
                className="form-input"
                style={{ flex: '1 1 120px', minWidth: 0 }}
                aria-label={t('trainingTemplates.filterTypeLabel')}
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
              >
                <option value="">{t('trainingTemplates.filterTypeAlle')}</option>
                {EXERCISE_TYPES.map(type => (
                  <option key={type} value={type}>{exerciseTypeLabel(type)}</option>
                ))}
              </select>
              <select
                className="form-input"
                style={{ flex: '1 1 140px', minWidth: 0 }}
                aria-label={t('exercises.alleSpiergroepen')}
                value={spiergroepActief ? filterSpiergroep : ''}
                disabled={!spiergroepActief}
                title={spiergroepActief ? undefined : t('trainingTemplates.filterSpiergroepUit')}
                onChange={e => setFilterSpiergroep(e.target.value)}
              >
                <option value="">{t('exercises.alleSpiergroepen')}</option>
                {MUSCLE_GROUPS.map(g => (
                  <option key={g} value={g}>{muscleGroupLabel(g)}</option>
                ))}
              </select>
            </div>

            <div className="template-library-list">
              {gefilterd.length === 0 ? (
                <p className="text-muted" style={{ fontSize: 13 }}>{t('exercises.geenOefeningen')}</p>
              ) : gefilterd.map(o => {
                const alToegevoegd = toegevoegdeIds.has(o.id);
                return (
                  <div key={o.id} className="template-library-row">
                    <div>
                      <strong>{o.name}</strong>
                      <small>
                        {muscleGroupLabel(o.muscle_group)}
                        {` · ${exerciseTypeLabel(o.exercise_type || 'kracht')}`}
                      </small>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={alToegevoegd}
                      onClick={() => voegToe(o)}
                    >
                      {alToegevoegd ? t('trainingTemplates.alToegevoegd') : t('trainingTemplates.toevoegen')}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 20 }}>
          <button type="button" className="btn btn-ghost" onClick={handleTerug}>
            {t('trainingTemplates.annuleren')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? '…' : t('trainingTemplates.opslaan')}
          </button>
        </div>
      </form>

      {annuleerConfirm && (
        <ConfirmModal
          title={t('trainingTemplates.annuleerTitel')}
          body={t('trainingTemplates.annuleerTekst')}
          confirmLabel={t('trainingTemplates.annuleerBevestigen')}
          danger
          onConfirm={() => navigate(`/coach/clients/${clientId}`)}
          onClose={() => setAnnuleerConfirm(false)}
        />
      )}
    </div>
  );
}
